/**
 * Inversiones y fotos de patrimonio (puro): líneas por fondo, liquidez a fin de mes y fotos mensuales que faltan.
 */
import { cashAt, investedUntil, monthEnd, planSnapshots, prevMonth, round2 } from '@pf/domain';
import type { Snapshot } from '@pf/workbook';
import { investments, type Computed } from './model.js';

export interface FundLine { fund: string; units: number; price: number | null; priceDate: string; value: number | null; invested: number; gain: number | null; returnPct: number | null }

export function fundLines(c: Computed): FundLine[] {
  const funds = [...c.wb.positions.map(p => p.fund)];
  c.wb.flows.forEach(f => { if (!funds.some(x => x.toLowerCase() === f.fund.toLowerCase())) funds.push(f.fund); });
  return funds.map(fund => {
    const mine = c.wb.flows.filter(f => f.fund.toLowerCase() === fund.toLowerCase());
    const pos = c.wb.positions.find(p => p.fund.toLowerCase() === fund.toLowerCase());
    const units = Math.round(mine.reduce((t, f) => t + (f.units ?? 0), 0) * 1e6) / 1e6;
    const invested = round2(mine.reduce((t, f) => t + f.amount, 0));
    const price = pos?.price ?? null;
    const value = price === null ? null : round2(units * price);
    const gain = value === null ? null : round2(value - invested);
    return { fund, units, price, priceDate: pos?.priceDate ?? '', value, invested, gain, returnPct: gain !== null && invested > 0 ? Math.round(gain / invested * 1000) / 10 : null };
  });
}

const cashAccounts = (c: Computed) => c.wb.accounts.map(a => ({ name: a.name, share: a.share, inNetWorth: a.inNetWorth, opening: a.opening + a.archived }));
const cashMovements = (c: Computed) => c.wb.movements.map(m => ({ account: m.account, date: m.date, amount: m.amount }));

/** Liquidez a fin de mes calculada con los movimientos del libro. */
export const cashAtMonthEnd = (c: Computed, month: string) => cashAt(cashAccounts(c), cashMovements(c), monthEnd(month));
/** Saldo de cada cuenta a fin de mes (entero, como en el extracto) y tu parte, para compararlo con el banco. */
export function accountsAtMonthEnd(c: Computed, month: string) {
  const end = monthEnd(month);
  return c.wb.accounts.map(a => {
    const balance = round2(a.opening + a.archived + c.wb.movements.filter(m => m.account === a.name && m.date && m.date <= end).reduce((t, m) => t + m.amount, 0));
    return { name: a.name, share: a.share, inNetWorth: a.inNetWorth, balance, mine: round2(balance * (a.share || 1)) };
  });
}

/** Capital aportado a fondos hasta fin de mes. */
export const investedAtMonthEnd = (c: Computed, month: string) => investedUntil(c.wb.flows, monthEnd(month));

/**
 * Fotos de meses cerrados que faltan, desde la primera que haya (o el primer mes con movimientos).
 * Si se toma en los 10 primeros días del mes siguiente, el último mes usa el valor actual de los fondos ('auto');
 * si no, lo aportado hasta entonces ('estimated').
 */
export function missingSnapshots(c: Computed, todayIso: string): Array<Omit<Snapshot, 'row'>> {
  const first = c.wb.snapshots.map(s => s.month).sort()[0] ?? c.months[c.months.length - 1];
  if (!first) return [];
  const currentMonth = todayIso.slice(0, 7);
  if (first > prevMonth(currentMonth)) return [];
  return planSnapshots({
    accounts: cashAccounts(c), movements: cashMovements(c), flows: c.wb.flows, snapshots: c.wb.snapshots,
    currentInvestments: c.wb.positions.length ? investments(c).value : null, currentMonth, firstMonth: first, todayDay: Number(todayIso.slice(8, 10)),
  });
}
